import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiRefreshCw, FiUpload, FiFileText, FiX, FiDownload } from "react-icons/fi";
import { MdOutlineArchive } from "react-icons/md";
import { createPDF } from "../../utils/pdfExport";
import * as XLSX from 'xlsx';
import { db, storage } from "../../firebase";
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, doc, limit } from "firebase/firestore";
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useLanguage } from "../../contexts/LanguageContext";
import Portal from "../../components/Portal";
import "../AdminPage.css";

const PEER_RESOURCE_CATEGORIES = [
  "Contraception",
  "Fertility Basics",
  "STI Awareness",
  "Mental Health in Reproductive Care",
  "Male Reproductive Health",
  "Pregnancy Prevention",
  "Gender-Based Violence",
  "General Reproductive Wellness"
];

// ---------------- Rejection Modal ----------------
function RejectionModal({ isOpen, onClose, onConfirm, isRejecting }) {
  const [rejectionReason, setRejectionReason] = useState("");
  const [error, setError] = useState("");
  const maxCharacters = 100;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rejectionReason.trim()) {
      setError("Rejection reason is required");
      return;
    }
    
    setError("");
    
    try {
      await onConfirm(rejectionReason.trim());
      setRejectionReason("");
      onClose();
    } catch (err) {
      setError(err.message || "Failed to reject resource. Please try again.");
    }
  };

  const handleClose = () => {
    setRejectionReason("");
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={handleClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3>Reject Resource</h3>
        </div>
        
        <p className="modal-subtitle">Please provide a reason for rejection:</p>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-field">
            <div className="textarea-wrapper">
              <textarea
                value={rejectionReason}
                onChange={(e) => {
                  setRejectionReason(e.target.value);
                  setError("");
                }}
                placeholder="Enter rejection reason..."
                className="modal-textarea full-width"
                required
                rows={4}
                disabled={isRejecting}
                maxLength={maxCharacters}
              />
              <div className="character-counter">
                {rejectionReason.length}/{maxCharacters}
              </div>
            </div>
          </div>
          
          {error && (
            <div className="modal-error">
              {error}
            </div>
          )}
          
          <div className="modal-actions">
            <button 
              type="button" 
              className="modal-btn modal-btn-cancel" 
              onClick={handleClose}
              disabled={isRejecting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className={`modal-btn modal-btn-confirm reject-btn ${isRejecting ? "loading" : ""}`}
              disabled={isRejecting}
            >
              {isRejecting ? (
                <span className="loading-spinner"></span>
              ) : (
                "Reject"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    </Portal>
  );
}

// ---------------- Export Modal ----------------
function ExportModal({ isOpen, onClose, onExport, isExporting, activeTab }) {
  const [selectedFormat, setSelectedFormat] = useState(null);

  useEffect(() => {
    const scrollContainer = document.querySelector('.mp-workspace');
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousContainerOverflow = scrollContainer?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollContainer) {
        scrollContainer.style.overflow = previousContainerOverflow;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay export-modal-overlay">
        <div className="modal-content export-modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">Export Report</h3>
            <button className="modal-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="export-modal-body">
            <p className="export-modal-subtitle">Select export format for {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} tab</p>

            <div className="export-format-list">
              <button
                className={`export-format-btn ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap pdf">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">PDF Report</span>
                  <span className="export-format-desc">Presentation-ready report</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'excel' ? 'selected' : ''}`}
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap excel">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">Excel .xlsx</span>
                  <span className="export-format-desc">Structured analytics workbook</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap csv">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">CSV .csv</span>
                  <span className="export-format-desc">Raw structured data</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ---------------- Archive Modal ----------------
function ArchiveModal({ isOpen, onClose, onConfirm, approval, isArchiving }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      // Error handling - modal will stay open
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Archive Resource</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#fee2e2", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <MdOutlineArchive size={32} color="#dc2626" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to archive this resource?</p>
          
          {approval && (
            <div className="content-preview" style={{ 
              background: "#f9fafb", 
              padding: "12px", 
              borderRadius: "8px", 
              marginTop: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>{approval.title}</div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                Category: {approval.category}
              </div>
            </div>
          )}
          
          <div style={{ 
            marginTop: "16px", 
            padding: "12px", 
            background: "#fef3c7", 
            borderRadius: "8px", 
            border: "1px solid #f59e0b"
          }}>
            <div style={{ fontSize: "14px", color: "#92400e", fontWeight: "600", textAlign: 'left' }}>
              This will:
            </div>
            <ul style={{ 
              margin: "8px 0 0 0", 
              paddingLeft: "20px", 
              fontSize: "14px", 
              color: "#92400e",
              textAlign: 'left'
            }}>
              <li>Remove resource from peer resources</li>
              <li>Resource will no longer be visible to peer educators</li>
              <li>Status will change to "Archived"</li>
            </ul>
          </div>
        </div>
        
        <div className="modal-actions">
          <button 
            type="button" 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isArchiving}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`modal-btn modal-btn-confirm archive-btn ${isArchiving ? "loading" : ""}`}
            onClick={handleConfirm}
            disabled={isArchiving}
            style={{
              backgroundColor: "#dc3545",
              borderColor: "#dc3545",
              color: "white"
            }}
          >
            {isArchiving ? (
              <span className="loading-spinner"></span>
            ) : (
              <>
                <MdOutlineArchive style={{ marginRight: '4px' }} />
                Archive
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

// ---------------- Restore Modal ----------------
function RestoreModal({ isOpen, onClose, onConfirm, approval, isRestoring }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      // Error handling - modal will stay open
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Restore Resource</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#d1fae5", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <FiRefreshCw size={32} color="#059669" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to restore this resource?</p>
          
          {approval && (
            <div className="content-preview" style={{ 
              background: "#f9fafb", 
              padding: "12px", 
              borderRadius: "8px", 
              marginTop: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>{approval.title}</div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                Category: {approval.category}
              </div>
            </div>
          )}
          
          <div style={{ 
            marginTop: "16px", 
            padding: "12px", 
            background: "#d1fae5", 
            borderRadius: "8px", 
            border: "1px solid #10b981"
          }}>
            <div style={{ fontSize: "14px", color: "#065f46", fontWeight: "600", textAlign: 'left' }}>
              This will:
            </div>
            <ul style={{ 
              margin: "8px 0 0 0", 
              paddingLeft: "20px", 
              fontSize: "14px", 
              color: "#065f46",
              textAlign: 'left'
            }}>
              <li>Add resource back to peer resources</li>
              <li>Resource will be visible to peer educators again</li>
              <li>Status will change to "Active"</li>
            </ul>
          </div>
        </div>
        
        <div className="modal-actions">
          <button 
            type="button" 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isRestoring}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`modal-btn modal-btn-confirm restore-btn ${isRestoring ? "loading" : ""}`}
            onClick={handleConfirm}
            disabled={isRestoring}
            style={{
              backgroundColor: "#28a745",
              borderColor: "#28a745",
              color: "white"
            }}
          >
            {isRestoring ? (
              <span className="loading-spinner"></span>
            ) : (
              <>
                <FiRefreshCw style={{ marginRight: '4px' }} />
                Restore
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

function AdminPeerResources() {
  const navigate = useNavigate();
  const [allResources, setAllResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [currentActionResource, setCurrentActionResource] = useState(null);
  const [currentRejectId, setCurrentRejectId] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: "",
    type: "pdf",
    category: ""
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isApproving, setIsApproving] = useState(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(null);
  const [isRestoring, setIsRestoring] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Export handlers
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const reportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });

      switch (format) {
        case 'pdf':
          await exportToPDF(reportDate);
          break;
        case 'excel':
          await exportToExcel(reportDate);
          break;
        case 'csv':
          await exportToCSV(reportDate);
          break;
        default:
          throw new Error('Invalid export format');
      }
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToPDF = async (reportDate) => {
    const pdf = createPDF({
      title: "Peer Resources Management Report"
    });

    // Prepare data for table
    let headers = [];
    if (filterStatus === "all" || filterStatus === "Active") headers = ['Creator', 'Title', 'Category', 'Status'];
    if (filterStatus === "Archived") headers = ['Creator', 'Title', 'Category', 'Archived Date'];

    const rows = resources.map(resource => {
      const row = [];
      if (filterStatus === "all" || filterStatus === "Active") row.push(resource.creator, resource.title, resource.category, resource.status);
      if (filterStatus === "Archived") row.push(resource.creator, resource.title, resource.category, formatDate(resource.archivedAt));
      return row;
    });

    pdf.autoTable({
      head: [headers],
      body: rows
    });

    pdf.save(`peer-resources-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = async (reportDate) => {
    const workbook = XLSX.utils.book_new();

    const summaryData = [
      ['PEER RESOURCES MANAGEMENT REPORT'],
      ['Report Date', reportDate],
      ['Filter Status', filterStatus],
      [],
      ['RESOURCES SUMMARY'],
      ['Total Resources', resources.length],
      ['Active', resources.filter(res => res.status !== 'Archived').length],
      ['Archived', resources.filter(res => res.status === 'Archived').length],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Resources details
    let headers = [];
    if (filterStatus === "all" || filterStatus === "Active") headers = ['Creator', 'Title', 'Category', 'Status'];
    if (filterStatus === "Archived") headers = ['Creator', 'Title', 'Category', 'Archived Date'];

    const resourceData = [
      ['RESOURCE DETAILS'],
      ...[headers],
      ...resources.map(resource => {
        const row = [];
        if (filterStatus === "all" || filterStatus === "Active") row.push(resource.creator, resource.title, resource.category, resource.status);
        if (filterStatus === "Archived") row.push(resource.creator, resource.title, resource.category, formatDate(resource.archivedAt));
        return row;
      })
    ];
    const resourceSheet = XLSX.utils.aoa_to_sheet(resourceData);
    XLSX.utils.book_append_sheet(workbook, resourceSheet, 'Resources');

    XLSX.writeFile(workbook, `peer-resources-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = async (reportDate) => {
    let csvData = [
      ['PEER RESOURCES MANAGEMENT REPORT'],
      ['Report Date', reportDate],
      ['Filter Status', filterStatus],
      [],
      ['RESOURCES SUMMARY'],
      ['Total Resources', resources.length],
      ['Active', resources.filter(res => res.status !== 'Archived').length],
      ['Archived', resources.filter(res => res.status === 'Archived').length],
      [],
      ['RESOURCE DETAILS'],
    ];

    let headers = [];
    if (filterStatus === "all" || filterStatus === "Active") headers = ['Creator', 'Title', 'Category', 'Status'];
    if (filterStatus === "Archived") headers = ['Creator', 'Title', 'Category', 'Archived Date'];

    csvData.push(headers);
    csvData.push(...resources.map(resource => {
      const row = [];
      if (filterStatus === "all" || filterStatus === "Active") row.push(resource.creator, resource.title, resource.category, resource.status);
      if (filterStatus === "Archived") row.push(resource.creator, resource.title, resource.category, formatDate(resource.archivedAt));
      return row;
    }));

    const csvContent = csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `peer-resources-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, type: 'default' });

  // Action button tooltip handlers
  const handleActionMouseEnter = (e, text, type = 'default') => {
    setTooltip({
      visible: true,
      text: text,
      x: e.clientX - 150,
      y: e.clientY,
      type: type
    });
  };

  const handleActionMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX - 150,
        y: e.clientY
      }));
    }
  };

  const handleActionMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0, type: 'default' });
  };

  // Filter resources locally based on filterStatus, search, and category
  const resources = allResources.filter(res => {
    const matchesStatus = 
      filterStatus === "all" ? true :
      filterStatus === "Active" ? res.status !== "Archived" : 
      res.status === filterStatus;
    const matchesSearch = 
      search === "" || 
      res.title?.toLowerCase().includes(search.toLowerCase()) ||
      res.creator?.toLowerCase().includes(search.toLowerCase()) ||
      res.category?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === "" || res.category === categoryFilter;
    return matchesStatus && matchesSearch && matchesCategory;
  });

  useEffect(() => {
    if (showUploadModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showUploadModal]);

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, "resources"), orderBy("createdAt", "desc"), limit(100)); // Add limit
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllResources(data);
        setLoading(false);
        setIsRefreshing(false);
      },
      (error) => {
        // Error handling for real-time listener
        setLoading(false);
        setIsRefreshing(false);
      }
    );
    
    return () => {
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]); // refreshKey bump re-subscribes listener; live updates handle mutations automatically

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, search, categoryFilter]);

  const totalPages = Math.ceil(resources.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentResources = resources.slice(startIndex, endIndex);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);

  const handleApprove = async (resourceId) => {
    setIsApproving(resourceId);
    try {
      await updateDoc(doc(db, "resources", resourceId), {
        status: "Active",
        approvedAt: new Date().toISOString(),
        approvedBy: "Admin"
      });
      // Real-time listener will automatically update the UI
    } catch (error) {
      let errorMessage = "Failed to approve resource. Please try again.";
      if (error.code === 'permission-denied') {
        errorMessage = "You don't have permission to approve resources.";
      } else if (error.code === 'not-found') {
        errorMessage = "Resource not found. It may have been deleted.";
      } else if (!navigator.onLine) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      // eslint-disable-next-line no-restricted-globals
      alert(errorMessage);
    } finally {
      setIsApproving(null);
    }
  };

  const handleReject = (resourceId) => {
    setCurrentRejectId(resourceId);
    setShowRejectionModal(true);
  };

  const handleRejectConfirm = async (rejectionReason) => {
    if (!currentRejectId) return;
    setIsRejecting(true);
    try {
      await updateDoc(doc(db, "resources", currentRejectId), {
        status: "Rejected",
        rejectedAt: new Date().toISOString(),
        rejectedBy: "Admin",
        rejectReason: rejectionReason
      });
      // Real-time listener will automatically update the UI
      setCurrentRejectId(null);
    } catch (error) {
      let errorMessage = "Failed to reject resource. Please try again.";
      if (error.code === 'permission-denied') {
        errorMessage = "You don't have permission to reject resources.";
      } else if (error.code === 'not-found') {
        errorMessage = "Resource not found. It may have been deleted.";
      } else if (!navigator.onLine) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      throw new Error(errorMessage);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleArchive = (resource) => {
    setCurrentActionResource(resource);
    setShowArchiveModal(true);
  };

  const confirmArchive = async () => {
    if (!currentActionResource) return;
    setIsArchiving(currentActionResource.id);
    try {
      await updateDoc(doc(db, "resources", currentActionResource.id), {
        status: "Archived",
        archivedAt: new Date().toISOString(),
        archivedBy: "Admin"
      });
      // Real-time listener will automatically update the UI
      setCurrentActionResource(null);
    } catch (error) {
      let errorMessage = "Failed to archive resource. Please try again.";
      if (error.code === 'permission-denied') {
        errorMessage = "You don't have permission to archive resources.";
      } else if (error.code === 'not-found') {
        errorMessage = "Resource not found. It may have been deleted.";
      } else if (!navigator.onLine) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      // eslint-disable-next-line no-restricted-globals
      alert(errorMessage);
    } finally {
      setIsArchiving(null);
    }
  };

  const handleRestore = (resource) => {
    setCurrentActionResource(resource);
    setShowRestoreModal(true);
  };

  const confirmRestore = async () => {
    if (!currentActionResource) return;
    setIsRestoring(currentActionResource.id);
    try {
      await updateDoc(doc(db, "resources", currentActionResource.id), {
        status: "Active",
        restoredAt: new Date().toISOString(),
        restoredBy: "Admin"
      });
      // Real-time listener will automatically update the UI
      setCurrentActionResource(null);
    } catch (error) {
      let errorMessage = "Failed to restore resource. Please try again.";
      if (error.code === 'permission-denied') {
        errorMessage = "You don't have permission to restore resources.";
      } else if (error.code === 'not-found') {
        errorMessage = "Resource not found. It may have been deleted.";
      } else if (!navigator.onLine) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      // eslint-disable-next-line no-restricted-globals
      alert(errorMessage);
    } finally {
      setIsRestoring(null);
    }
  };

  const getAcceptedFileTypes = (type) => {
    switch(type) {
      case "pdf":
        return ".pdf";
      case "image":
        return ".jpg,.jpeg,.png";
      default:
        return "";
    }
  };

  const validateFileType = (file, type) => {
    const fileName = file.name.toLowerCase();
    switch(type) {
      case "pdf":
        return fileName.endsWith('.pdf');
      case "image":
        return fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png');
      default:
        return false;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (validateFileType(file, uploadForm.type)) {
        setSelectedFile(file);
        setFileError(null);
      } else {
        setFileError(`Invalid file type. Please upload a ${uploadForm.type} file.`);
        setSelectedFile(null);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      if (validateFileType(file, uploadForm.type)) {
        setSelectedFile(file);
        setFileError(null);
      } else {
        setFileError(`Invalid file type. Please upload a ${uploadForm.type} file.`);
        setSelectedFile(null);
      }
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    
    // Validate file is selected and type matches
    if (!selectedFile) {
      setFileError("Please select a file to upload.");
      return;
    }
    
    if (!validateFileType(selectedFile, uploadForm.type)) {
      setFileError(`Selected file type doesn't match chosen resource type.`);
      return;
    }

    setIsUploading(true);
    try {

      
      // Upload file to Firebase Storage
      const fileName = `${Date.now()}-${selectedFile.name}`;

      
      const fileStorageRef = storageRef(storage, `resources/${fileName}`);

      
      const snapshot = await uploadBytesResumable(fileStorageRef, selectedFile);

      
      // Get download URL
      const downloadURL = await getDownloadURL(fileStorageRef);

      
      // Save to Firestore
      await addDoc(collection(db, "resources"), {
        title: uploadForm.title,
        type: uploadForm.type,
        category: uploadForm.category,
        file: selectedFile.name,
        fileUrl: downloadURL,
        storagePath: fileStorageRef.fullPath,
        creator: "Admin",
        creatorId: "admin",
        status: "Active",
        createdAt: new Date().toISOString()
      });
      
      // Real-time listener will automatically update the UI
      setUploadForm({ title: "", type: "pdf", category: "" });
      setSelectedFile(null);
      setFileError(null);
      setShowUploadModal(false);
      
    } catch (error) {
      let errorMessage = "Failed to upload resource. Please try again.";
      if (error.code === 'storage/unauthorized') {
        errorMessage = "You don't have permission to upload files.";
      } else if (error.code === 'storage/canceled') {
        errorMessage = "Upload was cancelled.";
      } else if (!navigator.onLine) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      // eslint-disable-next-line no-restricted-globals
      alert(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "Invalid Date";
    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return (
      <>
        {dateStr}<br />{timeStr}
      </>
    );
  };

  const getStatusBadge = (status) => {
    const displayStatus = status === "Archived" ? "Archived" : "Active";
    const statusClass = displayStatus.toLowerCase();
    return (
      <span className={`admin-status-badge admin-status-badge-${statusClass}`}>
        {displayStatus}
      </span>
    );
  };

  const getFilterCount = (status) => {
    return allResources.filter(r => r.status === status).length;
  };

  // Handle opening resource review
  const openResourceReview = (resource) => {
    navigate(`/admin/resource-review/${resource.id}`);
  };

  return (
    <>
    <div className="account-management-container">
      {/* ── Custom Tooltip for Action Buttons ── */}
      {tooltip.visible && (
        <div
          className={`admin-action-tooltip ${tooltip.type}`}
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.text}
        </div>
      )}
      <div className="content-library-header-custom" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Administrator - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              Administrator
            </h2>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div style={{ textAlign: 'right' }}>
          <h2 style={{
            margin: 0,
            fontSize: '36px',
            fontWeight: '800',
            color: '#ffffff !important',
            background: 'none !important',
            WebkitBackgroundClip: 'unset !important',
            WebkitTextFillColor: '#ffffff !important',
            backgroundClip: 'unset !important',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Peer Resources Management
          </h2>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '18px',
            color: '#ffffff',
            fontWeight: '500',
            textShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            Manage peer educator resources and approval requests
          </p>
        </div>

        {/* CSS Animations */}
        <style>{`
          .content-library-header-custom h2 {
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: #ffffff !important;
            background-clip: unset !important;
          }
          .content-library-header-custom p {
            color: #ffffff !important;
          }
        `}</style>
      </div>

      <div className="admin-aura-top-controls" style={{ marginTop: 20 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
            placeholder="Search Creator, Title, Category..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className="admin-aura-select"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
        >
          <option value="">All Categories</option>
          {PEER_RESOURCE_CATEGORIES.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
        <button
          className="admin-primary-gradient-btn"
          onClick={() => {
            setIsRefreshing(true);
            setRefreshKey(k => k + 1);
          }}
          disabled={isRefreshing}
          style={{ opacity: isRefreshing ? 0.6 : 1, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
        >
          <FiRefreshCw className={isRefreshing ? "spinning" : ""} /> {isRefreshing ? "Refreshing..." : "Refresh"}
        </button>
        <button
          className="admin-primary-gradient-btn"
          onClick={() => setShowExportModal(true)}
        >
          <FiDownload /> Export Report
        </button>
        <button onClick={() => setShowUploadModal(true)} className="refresh-btn">
          <FiUpload /> Upload
        </button>
      </div>

      <div className="content-filter-tabs-wrapper">
        <div className="content-filter-tabs">
          <button
            className={`content-filter-tab ${filterStatus === "all" ? "active" : ""}`}
            onClick={() => setFilterStatus("all")}
          >
            All
            <span className="tab-count">{allResources.length}</span>
          </button>
          <button
            className={`content-filter-tab ${filterStatus === "Active" ? "active" : ""}`}
            onClick={() => setFilterStatus("Active")}
          >
            Active
            <span className="tab-count">
              {allResources.filter(res => res.status !== "Archived").length}
            </span>
          </button>
          <button
            className={`content-filter-tab ${filterStatus === "Archived" ? "active" : ""}`}
            onClick={() => setFilterStatus("Archived")}
          >
            Archived
            <span className="tab-count">{getFilterCount("Archived")}</span>
          </button>
        </div>
        
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <div style={{ fontSize: "14px", color: "#6b7280" }}>
            {resources.length > 0 ? (
              `${startIndex + 1}-${Math.min(endIndex, resources.length)} of ${resources.length}`
            ) : (
              '0 of 0'
            )}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button 
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={{ 
                backgroundColor: '#1e3a8a', 
                color: 'white', 
                border: 'none', 
                padding: '8px 12px', 
                borderRadius: '4px', 
                cursor: 'pointer',
                width: '32px'
              }}
            >
              ←
            </button>
            
            <button 
              onClick={handleNextPage}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{ 
                backgroundColor: '#1e3a8a', 
                color: 'white', 
                border: 'none', 
                padding: '8px 12px', 
                borderRadius: '4px', 
                cursor: 'pointer',
                width: '32px'
              }}
            >
              →
            </button>
          </div>
        </div>
      </div>

      <div className="admin-aura-table-wrapper" style={{ marginTop: 10 }}>
        <table className="admin-aura-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Type</th>
              <th>Date Created</th>
              <th>Status</th>
              {filterStatus === "Archived" && (
                <th>Archived Date</th>
              )}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <>
                {[...Array(10)].map((_, i) => (
                  <tr key={i} className="admin-aura-skeleton-row">
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 3) * 15}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '70%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '65%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-badge" /></td>
                    {filterStatus === "Archived" && <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '55%' }} /></td>}
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></td>
                  </tr>
                ))}
              </>
            ) : resources.length === 0 ? (
              <tr>
                <td colSpan={6 + (filterStatus === "Archived" ? 1 : 0)} style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                  No resources found for the selected filter.
                </td>
              </tr>
            ) : (
              currentResources.map((resource) => (
                <tr
                  key={resource.id}
                  onClick={() => openResourceReview(resource)}
                  style={{ cursor: 'pointer' }}
                  className="admin-aura-clickable-row"
                >
                  <td>{resource.title}</td>
                  <td>{resource.category}</td>
                  <td>{resource.type}</td>
                  <td>{formatDate(resource.createdAt)}</td>
                  <td>
                    <span className={`admin-aura-status ${resource.status === "Archived" ? "inactive" : "active"}`}>
                      <span className="admin-aura-status-dot" />
                      {resource.status === "Archived" ? "Archived" : "Active"}
                    </span>
                  </td>
                  {filterStatus === "Archived" && (
                    <td>{formatDate(resource.archivedAt)}</td>
                  )}
                  <td className="admin-aura-actions" style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="ma-action-btn cancel"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (resource.status === "Archived") {
                          handleRestore(resource);
                        } else {
                          handleArchive(resource);
                        }
                      }}
                      disabled={isArchiving === resource.id || isRestoring === resource.id}
                      aria-label={resource.status === "Archived" ? "Restore" : "Archive"}
                      onMouseEnter={(e) => { e.target.style.background = resource.status === "Archived" ? '#d1fae5' : '#fecaca'; e.target.style.color = resource.status === "Archived" ? '#059669' : '#dc2626'; handleActionMouseEnter(e, resource.status === "Archived" ? "Restore Resource" : "Archive Resource", resource.status === "Archived" ? 'approve' : 'delete'); }}
                      onMouseMove={handleActionMouseMove}
                      onMouseLeave={(e) => { e.target.style.background = resource.status === "Archived" ? '#ecfdf5' : '#fee2e2'; e.target.style.color = resource.status === "Archived" ? '#059669' : '#dc2626'; handleActionMouseLeave(); }}
                      style={{ background: resource.status === "Archived" ? '#ecfdf5' : '#fee2e2', color: resource.status === "Archived" ? '#059669' : '#dc2626', borderColor: resource.status === "Archived" ? '#10b981' : '#ef4444', border: '1px solid', borderRadius: '12px', opacity: isArchiving === resource.id || isRestoring === resource.id ? 0.5 : 1 }}
                    >
                      {isArchiving === resource.id || isRestoring === resource.id ? (
                        <span className="loading-spinner" style={{ fontSize: '16px' }}></span>
                      ) : (
                        resource.status === "Archived" ? <FiRefreshCw /> : <MdOutlineArchive />
                      )}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      <RejectionModal
        isOpen={showRejectionModal}
        onClose={() => { setShowRejectionModal(false); setCurrentRejectId(null); }}
        onConfirm={handleRejectConfirm}
        isRejecting={isRejecting}
      />
      
      <ArchiveModal
        isOpen={showArchiveModal}
        onClose={() => { setShowArchiveModal(false); setCurrentActionResource(null); }}
        onConfirm={confirmArchive}
        approval={currentActionResource}
        isArchiving={isArchiving}
      />
      
      <RestoreModal
        isOpen={showRestoreModal}
        onClose={() => { setShowRestoreModal(false); setCurrentActionResource(null); }}
        onConfirm={confirmRestore}
        approval={currentActionResource}
        isRestoring={isRestoring}
      />

      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" style={{ maxWidth: '650px', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ 
              background: 'linear-gradient(135deg, #1e40af 0%, #0f172a 100%)', 
              padding: '24px 28px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '22px', fontWeight: '700' }}>
                Upload Peer Resource
              </h3>
              <button 
                onClick={() => setShowUploadModal(false)} 
                style={{ 
                  background: 'rgba(255,255,255,0.15)', 
                  border: 'none', 
                  borderRadius: '12px', 
                  padding: '8px', 
                  cursor: 'pointer', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
              >
                <FiX style={{ color: '#fff', fontSize: '24px' }} />
              </button>
            </div>
            <form onSubmit={handleUploadSubmit} style={{ padding: '28px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>Title</label>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    required
                    style={{
                      padding: '14px 16px',
                      borderRadius: '14px',
                      border: '2px solid #e2e8f0',
                      fontSize: '15px',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>Type</label>
                    <select
                      value={uploadForm.type}
                      onChange={(e) => {
                        setUploadForm({ ...uploadForm, type: e.target.value });
                        setSelectedFile(null);
                        setFileError(null);
                      }}
                      style={{
                        padding: '14px 16px',
                        borderRadius: '14px',
                        border: '2px solid #e2e8f0',
                        fontSize: '15px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        background: '#fff'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="pdf">PDF</option>
                      <option value="image">Image</option>
                    </select>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>Category</label>
                    <select
                      value={uploadForm.category}
                      onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                      required
                      style={{
                        padding: '14px 16px',
                        borderRadius: '14px',
                        border: '2px solid #e2e8f0',
                        fontSize: '15px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        background: '#fff'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">Select a category</option>
                      <option value="Menstrual Wellness and Hygiene">Menstrual Wellness and Hygiene</option>
                      <option value="Contraception">Contraception</option>
                      <option value="Fertility Basics">Fertility Basics</option>
                      <option value="STI Awareness">STI Awareness</option>
                      <option value="Mental Health in Reproductive Care">Mental Health in Reproductive Care</option>
                      <option value="Male Reproductive Health">Male Reproductive Health</option>
                      <option value="Pregnancy Prevention">Pregnancy Prevention</option>
                      <option value="Gender-Based Violence">Gender-Based Violence</option>
                      <option value="General Reproductive Wellness">General Reproductive Wellness</option>
                    </select>
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>Import File</label>
                  <div
                    className={`file-upload-area ${isDragOver ? 'drag-over' : ''}`}
                    style={{
                      border: '2px dashed #cbd5e1',
                      borderRadius: '16px',
                      padding: '36px 24px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      backgroundColor: isDragOver ? '#eff6ff' : '#f8fafc'
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    <input
                      id="file-input"
                      type="file"
                      accept={getAcceptedFileTypes(uploadForm.type)}
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    {selectedFile ? (
                      <div style={{ color: '#1e293b' }}>
                        <FiFileText style={{ fontSize: '40px', marginBottom: '10px', color: '#1e40af' }} />
                        <div style={{ fontSize: '15px', fontWeight: '600' }}>{selectedFile.name}</div>
                      </div>
                    ) : (
                      <div style={{ color: '#64748b' }}>
                        <FiUpload style={{ fontSize: '40px', marginBottom: '10px' }} />
                        <div style={{ fontSize: '16px', fontWeight: '500' }}>Drag and drop a file here, or click to browse</div>
                        <div style={{ fontSize: '13px', marginTop: '6px', color: '#94a3b8' }}>
                          Accepted formats: {uploadForm.type === 'pdf' ? '.pdf' : 
                            uploadForm.type === 'image' ? '.jpg, .jpeg, .png' : 
                            '.doc, .docx'}
                        </div>
                      </div>
                    )}
                  </div>
                  {fileError && (
                    <div style={{ 
                      color: '#dc2626', 
                      fontSize: '14px', 
                      marginTop: '8px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      ⚠️ {fileError}
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowUploadModal(false);
                      setSelectedFile(null);
                      setFileError(null);
                    }}
                    style={{
                      padding: '14px 32px',
                      borderRadius: '16px',
                      border: 'none',
                      background: '#f1f5f9',
                      color: '#475569',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#e2e8f0';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#f1f5f9';
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={isUploading}
                    style={{
                      padding: '14px 32px',
                      borderRadius: '16px',
                      border: 'none',
                      background: isUploading ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      color: '#fff',
                      fontSize: '15px',
                      fontWeight: '700',
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      boxShadow: isUploading ? 'none' : '0 4px 16px rgba(59, 130, 246, 0.35)',
                      transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                      opacity: isUploading ? 0.7 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isUploading) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.45)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isUploading) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.35)';
                      }
                    }}
                  >
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>

    <ExportModal
      isOpen={showExportModal}
      onClose={() => setShowExportModal(false)}
      onExport={handleExport}
      isExporting={isExporting}
      activeTab={filterStatus}
    />
  </>
  );
}

export default AdminPeerResources;
